import { Link } from "react-router-dom";
import { Expand, Gauge, Heart, KeyRound, Star } from "lucide-react";
import type { Car } from "@/types/database";
import { SafeImage } from "@/components/ui/SafeImage";
import { carImageSrc, carTitle, formatNumber } from "@/lib/utils";

export function CarCard({ car }: { car: Car }) {
  return (
    <Link to={`/cars/${car.carID}`} className="group hover-raise block overflow-hidden rounded-3xl bg-canvas hover:bg-surface">
      <div className="relative aspect-[16/9]">
        <SafeImage
          src={carImageSrc(car.carImage)}
          alt={carTitle(car)}
          fill
          className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
          sizes="(max-width: 768px) 100vw, 25vw"
        />

        <div className="absolute right-3 top-3 flex gap-1.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-foreground backdrop-blur-sm">
            <Expand className="h-3.5 w-3.5" />
          </span>
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-foreground backdrop-blur-sm">
            <Heart className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>

      <div className="border-t border-border/60 px-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-bold">{carTitle(car)}</h3>
            <p className="mt-0.5 truncate text-xs text-muted">
              {[car.trim, car.transmission].filter(Boolean).join(" · ") || "Automatic"}
            </p>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-2.5 text-[10px] font-semibold text-muted">
          {car.shiftRating != null ? (
            <span className="inline-flex items-center gap-1 text-foreground">
              <Star className="h-3 w-3 fill-accent text-accent" />
              {car.shiftRating}
            </span>
          ) : null}
          {car.odometer != null ? (
            <span className="inline-flex items-center gap-1">
              <Gauge className="h-3 w-3" />
              {formatNumber(car.odometer)} mi
            </span>
          ) : null}
          {car.licensePlate ? (
            <span className="inline-flex items-center gap-1">
              <KeyRound className="h-3 w-3" />
              {car.licensePlate}
            </span>
          ) : null}
        </div>
      </div>
    </Link>
  );
}
